import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SessionsTableProps, SessionV2 } from '../../../types/dashboardV2';
import {
  getSessionStatusBadgeClass,
  getSessionStatusDotClass,
  formatSessionStatus,
} from '../../../shared/utils/statusUtils';
import {
  DataTable,
  type DataTableColumnMeta,
  type DataTableRenderers,
} from '../components/DataTable';
import { StatusBadgeCell, DateTimeCell, IdWithCopyCell, TableActionButton } from '../components/cells';
import { usePrivacy } from '../../../context/PrivacyContext';
import { MASK } from '../../../shared/utils/mask';

/**
 * Twelve columns don't fit a laptop viewport: below this the table scrolls
 * horizontally instead of squeezing headers into each other. The percentages
 * below are proportional to what each column actually needs at this width.
 */
export const SESSIONS_TABLE_MIN_WIDTH = '1260px';

export const SESSIONS_COLUMNS = [
  { key: 'sessionId', labelKey: 'dashboard.tables.sessions.sessionId', width: '7%', cellClassName: 'cell-id', skeletonVariant: 'id' },
  { key: 'stationId', labelKey: 'dashboard.tables.sessions.stationId', width: '11%', cellClassName: 'cell-id', skeletonVariant: 'id' },
  { key: 'connectors', labelKey: 'dashboard.tables.sessions.connectors', width: '6.5%', align: 'center', cellClassName: 'cell-option', tabularNums: true, skeletonVariant: 'option' },
  { key: 'status', labelKey: 'dashboard.tables.sessions.status', width: '10%', skeletonVariant: 'status' },
  { key: 'amount', labelKey: 'dashboard.tables.sessions.amount', width: '7%', align: 'center', cellClassName: 'cell-price', tabularNums: true, skeletonVariant: 'price' },
  { key: 'settled', labelKey: 'dashboard.tables.sessions.settled', width: '7.5%', align: 'center', cellClassName: 'cell-price', tabularNums: true, skeletonVariant: 'price' },
  { key: 'pricePerKwh', labelKey: 'dashboard.tables.sessions.pricePerKwh', width: '9.5%', align: 'center', cellClassName: 'cell-price', tabularNums: true, skeletonVariant: 'price' },
  { key: 'charged', labelKey: 'dashboard.tables.sessions.charged', width: '9%', align: 'center', cellClassName: 'cell-connectors', tabularNums: true, skeletonVariant: 'price' },
  { key: 'requested', labelKey: 'dashboard.tables.sessions.requested', width: '8.5%', align: 'center', cellClassName: 'cell-connectors', tabularNums: true, skeletonVariant: 'price' },
  { key: 'userId', labelKey: 'dashboard.tables.sessions.userId', width: '9.5%', cellClassName: 'cell-address', skeletonVariant: 'address' },
  { key: 'date', labelKey: 'dashboard.tables.sessions.date', width: '9.5%', align: 'right', skeletonVariant: 'id' },
  { key: 'actions', labelKey: 'dashboard.tables.actions', width: '5%', align: 'center', skeletonVariant: 'action' },
] as const satisfies readonly DataTableColumnMeta[];

/**
 * What the driver actually paid for energy. `amount` is only the prepay quote;
 * until a settle path runs the real figure is unknown, and "unknown" must not
 * read as "free" — hence a dash, not 0.00.
 */
const SettledAmountCell = ({ session, hidden }: { session: SessionV2; hidden: boolean }) => {
  const { t } = useTranslation();
  if (hidden) return <>{MASK}</>;
  if (session.settledAmount == null) {
    return <span title={t('dashboard.tables.sessions.settledPending')}>—</span>;
  }
  return <>{`${session.settledAmount.toFixed(2)} ₴`}</>;
};

const PricePerKwhCell = ({ session, hidden }: { session: SessionV2; hidden: boolean }) => {
  if (hidden) return <>{MASK}</>;

  if (session.premiumPricePerKwh != null) {
    return (
      <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
        <span style={{ textDecoration: 'line-through', color: 'var(--color-text-secondary, #888)', fontSize: '0.8em' }}>
          {session.stationPrice != null ? `${session.stationPrice.toFixed(2)} ₴` : '—'}
        </span>
        <span style={{ color: 'var(--color-accent, #f59e0b)', fontWeight: 600 }}>
          {session.premiumPricePerKwh.toFixed(2)} ₴
        </span>
      </span>
    );
  }
  return <>{session.stationPrice != null ? `${session.stationPrice.toFixed(2)} ₴` : '—'}</>;
};

export const SessionsTable = memo(function SessionsTable({
  sessions,
  total,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onViewDetails,
}: SessionsTableProps) {
  const { t } = useTranslation();
  const { hidden } = usePrivacy('sessions');

  const renderers = useMemo<DataTableRenderers<SessionV2, typeof SESSIONS_COLUMNS>>(
    () => ({
      sessionId: (s) => `#${s.sessionId}`,
      stationId: (s) => <span title={s.idTag}>{s.idTag}</span>,
      connectors: (s) => s.connectorId,
      status: (s) => (
        <StatusBadgeCell
          badgeClass={getSessionStatusBadgeClass(s.chargingStatus)}
          dotClass={getSessionStatusDotClass(s.chargingStatus)}
          label={formatSessionStatus(s.chargingStatus)}
        />
      ),
      amount: (s) => (hidden ? MASK : `${s.amount.toFixed(2)} ₴`),
      settled: (s) => <SettledAmountCell session={s} hidden={hidden} />,
      pricePerKwh: (s) => <PricePerKwhCell session={s} hidden={hidden} />,
      charged: (s) => (hidden ? MASK : `${s.energy.toFixed(2)} kWh`),
      requested: (s) => (hidden ? MASK : `${s.totalQuantity.toFixed(2)} kWh`),
      userId: (s) =>
        hidden ? (
          MASK
        ) : (
          <IdWithCopyCell value={s.userId} copyLabel={t('dashboard.tables.sessions.copyUserId')} />
        ),
      date: (s) => <DateTimeCell value={s.createdAt} />,
      actions: (s) => (
        <TableActionButton
          icon="visibility"
          onClick={() => onViewDetails(s)}
          ariaLabel={t('dashboard.tables.sessions.viewMetricsAria', { id: s.sessionId })}
        />
      ),
    }),
    [t, onViewDetails, hidden],
  );

  return (
    <DataTable
      columns={SESSIONS_COLUMNS}
      renderers={renderers}
      rows={sessions}
      rowKey={(s) => s.id}
      minWidth={SESSIONS_TABLE_MIN_WIDTH}
      dense
      pagination={{ total, page, rowsPerPage, onPageChange, onRowsPerPageChange }}
    />
  );
});

export default SessionsTable;
